import React, { useState } from "react";
import { PlayCircle, CheckCircle, MonitorPlay, BookOpen, Calendar, Video } from "lucide-react";
import CoachingModal from "../components/CoachingModal";

// Replace 'youtubeId' with the actual string found after 'v=' in a YouTube link.
// Example: For https://www.youtube.com/watch?v=dQw4w9WgXcQ, the ID is dQw4w9WgXcQ. b7bk4WNM-gI
const CURRICULUM = [
  { id: "v1", title: "Overview", youtubeId: "HhZUeXZBqgw", duration: "7:11" },
  { id: "v2", title: "Chart Settings", youtubeId: "MkFueLbz4-U", duration: "35:28" },
  { id: "v3", title: "Chart Types", youtubeId: "LUgneYGmRjI", duration: "15:59" },
  { id: "v4", title: "Watchlist", youtubeId: "U3ql2I9I_uU", duration: "16:02" },
  { id: "v5", title: "Drawings Tools", youtubeId: "ioJJwMRAzp0", duration: "25:24" },
  { id: "v6", title: "Indicators", youtubeId: "N4wwY352EZM", duration: "13:11" },
  { id: "v7", title: "Templates", youtubeId: "4i7LH1Q0Idk", duration: "13:28" },
  { id: "v8", title: "Layouts", youtubeId: "kIPORoppBKo", duration: "16:56" },
  { id: "v9", title: "Replay Tool", youtubeId: "_Lgh-5kD7tw", duration: "18:38" },
  { id: "v10", title: "Alerts", youtubeId: "gNpY1zEEDG8", duration: "16:20" },
  { id: "v11", title: "Quick Search", youtubeId: "b7bk4WNM-gI", duration: "7:05" },
  { id: "v12", title: "Paper Trading", youtubeId: "2PWx6P0Pbok", duration: "16:26" },
];

export default function UniversityPage() {
  const [activeVideo, setActiveVideo] = useState(CURRICULUM[0]);
  const [showCoachingModal, setShowCoachingModal] = useState(false);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      
      {/* Page Header */}
      <div className="mb-8 border-b border-gray-800 pb-6 flex flex-col md:flex-row md:justify-between md:items-end gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Trapound <span className="text-blue-500">University</span>
          </h1>
          <p className="text-gray-400 mt-2 text-lg max-w-2xl">
            Complete this curated prerequisite syllabus to master the TradingView interface before booking your 1-on-1 chart setup session.
          </p>
        </div>
        
        {/* Book Coaching Button */}
        <button 
          onClick={() => setShowCoachingModal(true)}
          className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-500/20 whitespace-nowrap"
        >
          <Calendar className="w-5 h-5" />
          Book 1-on-1 Setup
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        
        {/* Left Column: Walled Garden YouTube Player */}
        <div className="flex-1">
          <div className="bg-black rounded-2xl overflow-hidden shadow-2xl border border-gray-800 relative aspect-video">
            <iframe
              key={activeVideo.youtubeId} // Forces iframe reload when navigating
              src={`https://www.youtube-nocookie.com/embed/${activeVideo.youtubeId}?rel=0&modestbranding=1`}
              title={activeVideo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full border-0"
            ></iframe>
          </div>

          <div className="mt-6 bg-gray-800/50 p-6 rounded-2xl border border-gray-700">
            <h2 className="text-2xl font-bold">{activeVideo.title}</h2>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Video className="w-4 h-4 text-blue-400" />
                Curated Lesson
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4 text-purple-400" />
                {activeVideo.duration}
              </span>
            </div>
            <p className="mt-3 text-gray-300 leading-relaxed">
              Open your TradingView workspace in a separate window to follow along with this module. 
              Write down any questions about <strong>{activeVideo.title}</strong> so we can address them during your 1-on-1 coaching session.
            </p>
          </div>
        </div>

        {/* Right Column: Curriculum Playlist */}
        <div className="lg:w-96 w-full flex-shrink-0">
          <div className="bg-gray-800/80 rounded-2xl border border-gray-700 overflow-hidden shadow-xl sticky top-24">
            
            <div className="p-4 border-b border-gray-700 bg-gray-800">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-blue-500" />
                Prerequisite Path
              </h3>
              <p className="text-xs text-gray-400 mt-1">12 Modules • Required before coaching</p>
            </div>

            <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
              {CURRICULUM.map((lesson, index) => {
                const isActive = activeVideo.id === lesson.id;
                
                return (
                  <button
                    key={lesson.id}
                    onClick={() => setActiveVideo(lesson)}
                    className={`w-full text-left p-4 flex items-start gap-3 border-b border-gray-700/50 transition-all ${
                      isActive 
                        ? "bg-blue-900/20 border-l-4 border-l-blue-500" 
                        : "hover:bg-gray-700/50 border-l-4 border-l-transparent"
                    }`}
                  >
                    <div className="mt-1">
                      {isActive ? (
                        <PlayCircle className="w-5 h-5 text-blue-400" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <h4 className={`font-semibold text-sm ${isActive ? "text-blue-300" : "text-gray-200"}`}>
                        {index + 1}. {lesson.title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">Video • {lesson.duration}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

      </div>
      
      {/* Render the Coaching Modal */}
      {showCoachingModal && (
        <CoachingModal onClose={() => setShowCoachingModal(false)} />
      )}
    </div>
  );
}